/**
 * §rbac (2026-05-08) — given an operator's effective permissions,
 * resolve the first route in the sidebar order that they can view.
 *
 * Used in two places:
 *   * Login.tsx — destination after a successful sign-in. The
 *     previous default (``/inbox``) traps users who don't have
 *     ``conversations.view`` (they hit /inbox → Guard redirects to
 *     /overview → they don't have ``overview.view`` either → blank).
 *   * permissions.tsx ``Guard`` — fallback when an operator hits a
 *     URL they can't view. Beats redirecting to a known route they
 *     also can't view.
 *
 * The ordering mirrors the sidebar in ``layout/AppShell.tsx`` so the
 * landing page is intuitive: Today section first, then Agent, then
 * Manage. If none of the listed routes is accessible, returns
 * ``"/no-access"`` — a small dead-end page that explains the
 * situation and offers logout (so the user isn't stuck in an
 * invisible redirect loop).
 */

import type { Permission } from "./rbac";

interface Destination {
  path: string;
  permission: Permission;
}

// Same order as NAV in AppShell.tsx. Master operators don't carry
// any of the day-to-day perms (their role is master.* only), so the
// master destination has to come BEFORE the customer-tenant routes —
// otherwise a master admin's first accessible route would resolve to
// /no-access. Other operators don't carry master.* and so skip past
// the master entry to their normal landing.
const ROUTES_BY_PRIORITY: Destination[] = [
  { path: "/master/tenants", permission: "master.tenants.view" },
  { path: "/overview",   permission: "overview.view" },
  { path: "/inbox",      permission: "conversations.view" },
  { path: "/live-calls", permission: "live_calls.view" },
  { path: "/orders",     permission: "orders.view" },
  { path: "/support",    permission: "support.view" },
  { path: "/customers",  permission: "customers.view" },
  { path: "/products",   permission: "products.view" },
  { path: "/agent-studio", permission: "agent.view" },
  { path: "/test-chat",  permission: "test_chat.view" },
  { path: "/test-voice", permission: "test_voice.view" },
  { path: "/analytics",  permission: "analytics.view" },
  { path: "/reports",    permission: "reports.view" },
  { path: "/settings/channels", permission: "settings.view" },
  // §v3.settings-hub (2026-05-25) — Users + Roles & permissions are now
  // tabs UNDER Settings. Land directly on the nested tab so post-login /
  // Guard-fallback routing skips the /users → /settings/users redirect
  // hop. (Security has no permission, so it never appears as a landing
  // target here — it's reachable via the Settings sidebar entry.)
  { path: "/settings/members", permission: "operators.view" },
  { path: "/settings/roles", permission: "roles.view" },
];

export const NO_ACCESS_ROUTE = "/no-access";

export function firstAccessibleRoute(
  permissions: readonly string[] | null | undefined,
): string {
  if (!permissions || permissions.length === 0) return NO_ACCESS_ROUTE;
  const set = new Set(permissions);
  for (const dest of ROUTES_BY_PRIORITY) {
    if (set.has(dest.permission)) return dest.path;
  }
  return NO_ACCESS_ROUTE;
}
